import { useState } from 'react';
import ModelProvider, { useModel } from '../ModelProvider';
import CheckLine from './PreflightFinding';
import { describeCheck } from '../engines/preflightCheck';
import { indexGlByLeafKey, ownedLabelFor } from '../utils/leafLabels';
import { formatMonthLabel } from '../engines/dateUtils';

/* ===========================================================================================
   HEALTH CHECK -- the Admin tab (W-035), SUPER ADMIN ONLY (the gate is AdminPage's, via
   utils/audience.js). It answers "does this company's model reconcile?" for the company picked
   in the company switcher.

   ONE PRE-FLIGHT, NOT TWO. Like the Go Live panel, it mounts its OWN ModelProvider for the
   company and READS the verdict that provider produces (engines/preflightCheck.js). Mounting
   it when the tab opens -- and again on "Re-check" -- means the verdict is always a FRESH
   build, never the in-context one a runOrchestrator recalculation leaves stale.

   A PROBLEM is a BLOCKING finding; a NOTE is a WARNING (grey, never styled as a problem). A
   check that could not run, or a model that could not be built, is said so -- never a green
   summary. On a LIVE company each finding shows the register's live fix (never "re-open the
   wizard"); offenders are named by their line's label, the raw form kept in the drawer.
   =========================================================================================== */

const FONT = "'DM Sans',-apple-system,sans-serif";
const DOT = ' \u00B7 ';

const box = { border: '1px solid #E0E0E0', borderRadius: 6, padding: 16, fontFamily: FONT, fontSize: 13 };

/* A check whose inputs were missing reports a WARNING whose `what` says "not evaluated" and names no
   offender (preflightCheck: P9, P10, P13, P14, P16). It is shown as such, not as a finding. */
export function isNotEvaluated(f) {
    return !!f && f.severity !== 'BLOCKING' && /not evaluated/i.test(f.what || '') && !(f.offenders || []).length;
}

function fmtTime(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/* The one summary line. Returns { kind: 'ok' | 'problems' | 'error', text }. */
export function healthSummary(pf) {
    if (!pf) return null;
    if (pf.errored) return { kind: 'error', text: 'The health check could not run: ' + (pf.error || 'unknown error') };
    const failures = pf.failures || [];
    const problems = failures.filter(f => f.severity === 'BLOCKING').length;
    const notes = failures.length - problems;
    const head = problems === 0 ? 'No problems found' : (problems === 1 ? '1 problem found' : problems + ' problems found');
    const tail = notes === 1 ? '1 note' : notes + ' notes';
    const at = fmtTime(pf.checkedAt);
    return { kind: problems ? 'problems' : 'ok', text: head + DOT + tail + (at ? DOT + 'checked ' + at : '') };
}

/* Offenders in plain English: the line by its OWNER's label (gl_line_mappings.display_name via
   utils/leafLabels.js), else the statement row's label; the month as a month; "(all zero)" for an
   all-zero series. Never JSON -- the raw form is in the "Technical detail" drawer. */
export function makeOffenderFormatter(raw) {
    const byKey = indexGlByLeafKey(raw && raw.glMappings);
    const rowLabel = new Map();
    for (const arr of [raw && raw.isLines, raw && raw.bsLines, raw && raw.cfsLines]) {
        for (const l of (arr || [])) if (l && l.id && l.label && !rowLabel.has(l.id)) rowLabel.set(l.id, l.label);
    }
    const meta = (raw && raw.bsActualsMeta) || {};
    const labelOf = k => {
        if (k == null) return null;
        const s = String(k);
        return ownedLabelFor(byKey, s) || rowLabel.get(s) || (meta[s] && meta[s].label) || null;
    };
    // a pair of lines ("deferred_rev_x / rev_x", P16's I1 identities) is named part by part
    const nameOf = v => String(v).split(' / ').map(p => labelOf(p.trim()) || p.trim()).join(' / ');
    const money = n => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const KEY_FIELDS = ['key', 'line', 'leaf', 'is_line', 'gl_line_key', 'bs_line_key'];
    return (o) => {
        if (o == null) return '';
        if (typeof o !== 'object') return labelOf(o) || String(o);
        const used = new Set(['label', 'name', 'why', 'reason', 'detail']);
        let head = o.label || o.name || null;
        for (const k of KEY_FIELDS) {
            if (o[k] == null) continue;
            used.add(k);
            if (!head) head = nameOf(o[k]);
        }
        /* W-086 (filed) / W-154 (2026-10-05): an id is NEVER shown as money ("19.00") and never beside a name -- the offender is
           named by its label; only a nameless offender falls back to "#<id>". */
        if (o.id != null) { used.add('id'); if (!head) head = labelOf(o.id) || ('#' + o.id); }
        if (!head && o.field) { used.add('field'); head = String(o.field).replace(/_/g, ' '); }
        const parts = [];
        if (head) parts.push(head);
        /* W-154 / W-123 / W-138: an offender's `what` is its plain-English sentence -- shown as itself (not "what ..."), and alone
           when it already opens with the name. */
        if (typeof o.what === 'string' && o.what) { used.add('what'); if (head && o.what.startsWith(head)) parts.length = 0; parts.push(o.what); }
        if (o.month) { used.add('month'); parts.push(formatMonthLabel(o.month, 'shortYear')); }
        if (o.nonzero === false) { used.add('nonzero'); parts.push('(all zero)'); }
        else if (o.nonzero === true) { used.add('nonzero'); parts.push('(carries dollars)'); }
        if (o.identity) { used.add('identity'); parts.push('(identity ' + o.identity + ')'); }
        for (const [k, v] of Object.entries(o)) {
            if (used.has(k) || v == null || typeof v === 'object') continue;
            const isId = /(^|_)id$/.test(k);   // W-086: an identifier is a number, never money
            parts.push(k.replace(/_/g, ' ') + ' ' + (isId ? '#' + v : typeof v === 'number' ? money(v) : String(v)));
        }
        if (o.detail) parts.push('-- ' + o.detail);
        return parts.join(' ');
    };
}

const SUMMARY_STYLE = {
    ok: { background: '#E8F5E9', border: '1px solid #A5D6A7', color: '#1B5E20' },
    problems: { background: '#FDECEA', border: '1px solid #F5C6C0', color: '#B71C1C' },
    error: { background: '#FDECEA', border: '1px solid #F5C6C0', color: '#B71C1C' },
    running: { background: '#F5F6FA', border: '1px solid #E0E0E0', color: '#666' },
};
const PROBLEM_BOX = { background: '#FDECEA', border: '1px solid #F5C6C0', borderRadius: 6, padding: 12, marginTop: 10, color: '#B71C1C' };
const NOTE_BOX = { background: '#F5F6FA', border: '1px solid #E0E0E0', borderRadius: 6, padding: 12, marginTop: 10, color: '#555' };

/* The verdict view. Exported so the verify can render it inside a supplied context. */
export function HealthInner({ live }) {
    const { preflight, loading, initialized, modelState, modelError, rawData } = useModel();

    let summary;
    if (preflight) summary = healthSummary(preflight);
    else if (modelState === 'error') summary = { kind: 'error', text: 'The health check could not run: the model could not be built (' + (modelError || 'unknown error') + ')' };
    else if (modelState === 'empty' && initialized && !loading) summary = { kind: 'error', text: 'The health check could not run: this company has no model to check yet (onboarding is incomplete).' };
    else summary = { kind: 'running', text: 'Building the model and running the health check\u2026' };

    const failures = (preflight && !preflight.errored && preflight.failures) || [];
    const problems = failures.filter(f => f.severity === 'BLOCKING');
    const notes = failures.filter(f => f.severity !== 'BLOCKING');
    const fmt = makeOffenderFormatter(rawData);

    return (
        <div>
            <div data-health-summary={summary.kind} style={{ ...SUMMARY_STYLE[summary.kind], borderRadius: 6, padding: 12, fontWeight: 700 }}>
                {summary.text}
            </div>
            {problems.map((f, i) => (
                <div key={'p' + i} data-health-item="problem" style={PROBLEM_BOX}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>PROBLEM</div>
                    <CheckLine f={f} tone="#7A1512" live={live} formatOffender={fmt} />
                </div>
            ))}
            {notes.map((f, i) => isNotEvaluated(f) ? (
                <div key={'n' + i} data-health-item="not-evaluated" style={NOTE_BOX}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>NOT EVALUATED</div>
                    <div style={{ marginTop: 4 }}>
                        <strong>{describeCheck(f.id, f.severity).name}</strong> could not be checked this run.
                    </div>
                    <div style={{ marginTop: 2, fontSize: 11, opacity: 0.8 }}>{f.what}</div>
                </div>
            ) : (
                <div key={'n' + i} data-health-item="note" style={NOTE_BOX}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>NOTE</div>
                    <CheckLine f={f} tone="#555" live={live} formatOffender={fmt} />
                </div>
            ))}
        </div>
    );
}

/* The tab. `company` is the company picked in the company switcher (App.js), or null. */
export default function HealthCheckPanel({ company }) {
    const [run, setRun] = useState(0);
    if (!company) {
        return (
            <div style={{ fontFamily: FONT, padding: 24, color: '#666' }}>
                Pick a company in the company switcher, then open this tab to check it.
            </div>
        );
    }
    const live = company.is_engine_ready === true;
    return (
        <div style={{ fontFamily: FONT }}>
            <h3 style={{ color: '#1A1C2E', fontSize: 18, margin: '0 0 6px' }}>Health Check {'\u2014'} {company.name}</h3>
            <p style={{ color: '#666', fontSize: 13, margin: '0 0 12px', maxWidth: 760 }}>
                Builds this company&rsquo;s model fresh and runs its own checks that the statements add up and agree with
                each other. Only you can see this tab.
            </p>
            <button onClick={() => setRun(r => r + 1)} style={{
                fontFamily: FONT, fontWeight: 600, fontSize: 13, padding: '6px 14px', borderRadius: 6,
                border: '1px solid #D1D5DB', background: '#fff', color: '#1A1C2E', cursor: 'pointer', marginBottom: 12,
            }}>Re-check</button>
            <div style={box}>
                <ModelProvider companyId={company.id} key={company.id + ':' + run} companyName={company.name}>
                    <HealthInner live={live} />
                </ModelProvider>
            </div>
        </div>
    );
}
